import { useEffect, useRef, useState } from "react";

import { PAGE_SIZE } from "../components/callsExplorer/callsExplorer.constants";
import { CallsService } from "../services/calls.service";
import { CallFilters, CallRecord } from "../types/calls.types";

export const useCallsList = (filters: CallFilters) => {
  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalCountCapped, setTotalCountCapped] = useState(false);

  const filtersKey = JSON.stringify(filters);

  // New calls keep arriving live, so paging with a plain offset drifts once
  // you're past page 1 - each request re-runs the query against a table
  // that's grown since, silently skipping or duplicating rows. Snapshotting
  // the most recent call_timestamp seen on the first page and pinning it as
  // end_time on every later page in the session freezes the result set, so
  // offset-based paging stays consistent. Only applies when the user hasn't
  // set their own end_time filter (that's already a fixed boundary).
  const snapshotEndTimeRef = useRef<string | null>(null);

  // `count` is now only computed and returned on the first page (offset 0) -
  // the checkpointed end_time already pins the result set, so re-counting on
  // every "load more" would be redundant compute. Cache it from page 1 and
  // reuse it for hasMore on later pages instead of trusting response.count,
  // which is omitted once offset > 0.
  const totalCountRef = useRef(0);
  // When capped, total_count is just the cap value (e.g. 10,000), not the
  // real total - there may be more rows beyond it, so hasMore can't be
  // derived by comparing offset against it like the uncapped case.
  const totalCountCappedRef = useRef(false);

  const fetchPage = async (nextOffset: number, append: boolean) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    try {
      // Only pin to the snapshotted end_time when paginating (append) into an
      // already-fetched result set. A fresh page-0 fetch - whether from a
      // filter change or the manual refresh button - must not inherit the
      // *previous* session's stale snapshot, or it'll silently miss any
      // calls that arrived since that old snapshot was taken.
      const response = await CallsService.listCalls({
        ...filters,
        end_time: append
          ? (filters.end_time ?? snapshotEndTimeRef.current ?? undefined)
          : filters.end_time,
        limit: PAGE_SIZE,
        offset: nextOffset,
      });

      if (!append) {
        snapshotEndTimeRef.current = filters.end_time
          ? null
          : response.data.reduce<string | null>(
              (latest, call) =>
                !latest || call.call_timestamp > latest ? call.call_timestamp : latest,
              null,
            );
        totalCountRef.current = response.total_count ?? 0;
        totalCountCappedRef.current = response.total_count_capped ?? false;
        setTotalCount(totalCountRef.current);
        setTotalCountCapped(totalCountCappedRef.current);
      }

      setCalls((prev) => (append ? [...prev, ...response.data] : response.data));
      setOffset(nextOffset);
      setHasMore(
        totalCountCappedRef.current
          ? response.data.length === PAGE_SIZE
          : nextOffset + response.data.length < totalCountRef.current,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load calls.");
      if (!append) setCalls([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    // Fetching from an effect is intended here; fetchPage flips the loading
    // flags before it awaits the request
    void fetchPage(0, false);
    // Re-fetch from the start whenever the filter set changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  return {
    calls,
    loading,
    loadingMore,
    error,
    hasMore,
    totalCount,
    totalCountCapped,
    loadMore: () => fetchPage(offset + PAGE_SIZE, true),
    refetch: () => fetchPage(0, false),
  };
};
